import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import { signalsService } from '../../services/signalsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { MdAdd } from 'react-icons/md';
import CollapsibleSection from './components/CollapsibleSection';
import ConsolidatedSummaryCard from './components/ConsolidatedSummaryCard';
import PlainEnglishSummary from './components/PlainEnglishSummary';
import PriceChartWithOverlays from './components/PriceChartWithOverlays';
import IndicatorPanel from './components/IndicatorPanel';
import SignalBadge from './components/SignalBadge';

const formatNum = (v, dec = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(dec));

const SymbolDetailPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await signalsService.getSymbol(symbol);
      setHistory(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(e.response?.data?.error || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { load(); }, [load]);

  const handleRerun = useCallback(async () => {
    setRerunning(true);
    try {
      await signalsService.recompute(symbol);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setRerunning(false);
    }
  }, [symbol, load]);

  const handleAdd = useCallback(async () => {
    setAdding(true);
    setError(null);
    try {
      await signalsService.addSymbol(symbol);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || `Could not add ${symbol}.`);
    } finally {
      setAdding(false);
    }
  }, [symbol, load]);

  if (loading) return <LoadingSpinner message={`Loading ${symbol}…`} />;

  if (notFound) {
    return (
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-8 text-center max-w-xl mx-auto">
        <div className="font-serif text-xl text-gray-900 dark:text-gray-100 mb-2">{symbol}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Not in our coverage yet. We can fetch its OHLCV history from yfinance / FMP and compute signals now.
        </div>
        {error && <div className="text-xs text-red-500 mb-3">{error}</div>}
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => navigate('/signals')}
            className="text-xs px-4 py-2 rounded border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Back to Best Picks
          </button>
          <button
            onClick={handleAdd}
            disabled={adding}
            className="text-xs px-4 py-2 rounded bg-[#c9a96e] text-white hover:bg-[#b8985d] disabled:opacity-40 flex items-center gap-1"
          >
            {adding ? (
              <span className="inline-block w-3 h-3 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
            ) : (
              <MdAdd />
            )}
            {adding ? 'Fetching… (10–30s)' : 'Add to Coverage'}
          </button>
        </div>
      </div>
    );
  }

  if (error) return <div className="text-sm text-red-500 p-4">{error}</div>;
  if (!history) return null;

  const ind = history.indicators_latest || {};
  const s = history.series || {};
  const dates = s.date || [];

  // Per-indicator panel trace builders
  const macdTraces = [
    { type: 'bar', x: dates, y: s.macd_hist, name: 'Hist',
      marker: { color: (s.macd_hist || []).map(v => (v > 0 ? '#22c55e' : '#ef4444')) } },
    { type: 'scatter', mode: 'lines', x: dates, y: s.macd, name: 'MACD', line: { color: '#3b82f6', width: 1.5 } },
    { type: 'scatter', mode: 'lines', x: dates, y: s.macd_signal, name: 'Signal', line: { color: '#f59e0b', width: 1.5 } },
  ];
  const rsiTraces = [
    { type: 'scatter', mode: 'lines', x: dates, y: s.rsi_14, name: 'RSI 14', line: { color: '#8b5cf6', width: 1.5 } },
  ];
  const stochTraces = [
    { type: 'scatter', mode: 'lines', x: dates, y: s.stoch_k, name: '%K', line: { color: '#06b6d4', width: 1.5 } },
    { type: 'scatter', mode: 'lines', x: dates, y: s.stoch_d, name: '%D', line: { color: '#f97316', width: 1.5 } },
  ];
  const adxTraces = [
    { type: 'scatter', mode: 'lines', x: dates, y: s.adx, name: 'ADX', line: { color: '#fde047', width: 1.5 } },
    { type: 'scatter', mode: 'lines', x: dates, y: s.plus_di, name: '+DI', line: { color: '#22c55e', width: 1 } },
    { type: 'scatter', mode: 'lines', x: dates, y: s.minus_di, name: '-DI', line: { color: '#ef4444', width: 1 } },
  ];
  const obvTraces = [
    { type: 'scatter', mode: 'lines', x: dates, y: s.obv, name: 'OBV', line: { color: '#a855f7', width: 1.5 }, fill: 'tozeroy', fillcolor: 'rgba(168, 85, 247, 0.1)' },
  ];
  const willTraces = [
    { type: 'scatter', mode: 'lines', x: dates, y: s.williams_r, name: 'Williams %R', line: { color: '#06b6d4', width: 1.5 } },
  ];
  const volumeColors = (s.close || []).map((c, i) => {
    if (i === 0 || s.close[i - 1] == null) return '#94a3b8';
    return c >= s.close[i - 1] ? '#22c55e' : '#ef4444';
  });
  const volumeTraces = [
    { type: 'bar', x: dates, y: s.volume, name: 'Volume', marker: { color: volumeColors }, opacity: 0.7 },
    { type: 'scatter', mode: 'lines', x: dates, y: s.volume_ratio, name: 'Vol ratio', yaxis: 'y2', line: { color: '#fbbf24', width: 1 } },
  ];

  const pct = ind.pct_change;
  const pctColor = pct == null
    ? 'text-gray-400'
    : pct > 0 ? 'text-green-600 dark:text-green-400'
    : pct < 0 ? 'text-red-500 dark:text-red-400'
    : 'text-gray-500';

  return (
    <div>
      {/* Back link */}
      <button
        onClick={() => navigate('/signals')}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#c9a96e] mb-3"
      >
        <MdArrowBack /> Back
      </button>

      {/* Symbol header — ticker + name + badge in a single, balanced row */}
      <div className="flex items-center gap-3 mb-5 min-w-0">
        <div className="font-serif text-2xl md:text-3xl text-gray-900 dark:text-gray-100 leading-none shrink-0">
          {history.symbol}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 truncate flex-1 min-w-0" title={history.name}>
          {history.name}
        </div>
        <SignalBadge signal={history.signal} size="lg" />
      </div>

      {/* Stats — uniform typography across all four tiles. 2x2 mobile, 1x4 desktop. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 md:gap-6 mb-5 pb-4 border-b border-gray-200 dark:border-gray-800">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-1">Last Close</div>
          <div className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {formatNum(ind.close, 2)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-1">Change</div>
          <div className={`text-base md:text-lg font-semibold truncate ${pctColor}`}>
            {pct == null ? '—' : `${pct >= 0 ? '+' : ''}${formatNum(pct, 2)}%`}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-1">As of</div>
          <div className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {history.as_of_date}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-1">Sector</div>
          <div className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate" title={history.sector || '—'}>
            {history.sector || '—'}
          </div>
        </div>
      </div>

      <PlainEnglishSummary history={history} />

      <CollapsibleSection title="Indicator Vote Breakdown" onRerun={handleRerun} rerunning={rerunning} >
        <ConsolidatedSummaryCard summary={history} />
      </CollapsibleSection>

      <CollapsibleSection title="Price + Overlays">
        <PriceChartWithOverlays history={history} />
      </CollapsibleSection>

      <CollapsibleSection title="MACD (12, 26, 9)" >
        <IndicatorPanel
          title=""
          traces={macdTraces}
          refLines={[{ y: 0, color: '#94a3b8' }]}
          height={260}
          yFormat=".3f"
        />
      </CollapsibleSection>

      <CollapsibleSection title="RSI (14)" >
        <IndicatorPanel
          title=""
          traces={rsiTraces}
          refLines={[
            { y: 70, color: '#ef4444', label: 'Overbought 70' },
            { y: 30, color: '#22c55e', label: 'Oversold 30' },
          ]}
          height={220}
          yFormat=".0f"
        />
      </CollapsibleSection>

      <CollapsibleSection title="Stochastic (14, 3)" >
        <IndicatorPanel
          title=""
          traces={stochTraces}
          refLines={[
            { y: 80, color: '#ef4444', label: '80' },
            { y: 20, color: '#22c55e', label: '20' },
          ]}
          height={220}
          yFormat=".0f"
        />
      </CollapsibleSection>

      <CollapsibleSection title="ADX + DI (14)" >
        <IndicatorPanel
          title=""
          traces={adxTraces}
          refLines={[{ y: 25, color: '#94a3b8', label: 'Trend 25' }]}
          height={220}
          yFormat=".0f"
        />
      </CollapsibleSection>

      <CollapsibleSection title="Williams %R (14)" >
        <IndicatorPanel
          title=""
          traces={willTraces}
          refLines={[
            { y: -20, color: '#ef4444', label: 'Overbought -20' },
            { y: -80, color: '#22c55e', label: 'Oversold -80' },
          ]}
          height={220}
          yFormat=".0f"
        />
      </CollapsibleSection>

      <CollapsibleSection title="On-Balance Volume" >
        <IndicatorPanel title="" traces={obvTraces} height={220} yFormat=".0f" />
      </CollapsibleSection>

      <CollapsibleSection title="Volume" >
        <IndicatorPanel title="" traces={volumeTraces} height={220} yFormat=".0f" />
      </CollapsibleSection>

      <CollapsibleSection title="Key Levels" >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          {ind.fibonacci && (
            <div>
              <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-2">Fibonacci</div>
              <table className="w-full">
                <tbody>
                  {[0.0, 0.236, 0.382, 0.5, 0.618, 0.786, 1.0].map((r) => (
                    <tr key={r} className="border-b border-gray-100 dark:border-gray-900">
                      <td className="py-1 text-gray-500 dark:text-gray-400">{(r * 100).toFixed(1)}%</td>
                      <td className="py-1 text-right text-gray-900 dark:text-gray-100">
                        {formatNum(ind.fibonacci[`fib_${r.toFixed(3)}`], 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {ind.pivots && Object.keys(ind.pivots).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-2">Daily Pivots</div>
              <table className="w-full">
                <tbody>
                  {['R3', 'R2', 'R1', 'P', 'S1', 'S2', 'S3'].map((p) => (
                    <tr key={p} className="border-b border-gray-100 dark:border-gray-900">
                      <td className="py-1 text-gray-500 dark:text-gray-400">{p}</td>
                      <td className="py-1 text-right text-gray-900 dark:text-gray-100">
                        {formatNum(ind.pivots[p], 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {ind.support_resistance && (
            <div>
              <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-2">Rolling S/R</div>
              <div className="text-gray-700 dark:text-gray-300">
                <div className="font-semibold text-red-500 mb-1">Resistance</div>
                {(ind.support_resistance.resistance || []).map((r, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">×{r.count}</span>
                    <span>{formatNum(r.level, 2)}</span>
                  </div>
                ))}
                {(ind.support_resistance.resistance || []).length === 0 && <div className="text-gray-400">—</div>}
                <div className="font-semibold text-green-600 mt-3 mb-1">Support</div>
                {(ind.support_resistance.support || []).map((r, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">×{r.count}</span>
                    <span>{formatNum(r.level, 2)}</span>
                  </div>
                ))}
                {(ind.support_resistance.support || []).length === 0 && <div className="text-gray-400">—</div>}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default SymbolDetailPage;
